import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, Users, Award, ChevronRight, Zap, Shield, Brain } from 'lucide-react';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { ParallaxSection } from '../components/ParallaxSection';
export function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    setIsVisible(true);
  }, []);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-528">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-id="element-529">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900" data-id="element-530" />

        {/* Hero Image Layer */}
        <div className="absolute inset-0 opacity-30" style={{
        transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
        transition: 'transform 0.3s ease-out'
      }} data-id="element-531">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fpw6RhS85NEuG9pS87VG72/AI_Tech_Company_%281%29.jpg" alt="AI Technology Innovation" className="w-full h-full object-cover" data-id="element-532" />
        </div>

        {/* Animated pattern overlay */}
        <div className="absolute inset-0 opacity-10" data-id="element-533">
          <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} data-id="element-534" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 text-center" data-id="element-535">
          <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-536">
            {/* Logo in Hero */}
            <div className="mb-8 flex justify-center" data-id="element-537">
              <img src="https://cdn.magicpatterns.com/uploads/m3EzG3aGX3C8qvhqEQC9KZ/2.png" alt="Manuel Technologies" className="h-16 sm:h-20 md:h-24 w-auto opacity-90" data-id="element-538" />
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-400/20 rounded-full mb-8 backdrop-blur-sm animate-pulse" data-id="element-539">
              <Sparkles className="w-4 h-4 text-blue-400" data-id="element-540" />
              <span className="text-sm text-blue-300 font-bold" data-id="element-541">
                AI-Powered Digital Transformation
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-6 leading-tight" data-id="element-542">
              Driving Digital
              <br data-id="element-543" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 animate-gradient" data-id="element-544">
                Transformation
              </span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-545">
              Enterprise-grade technology solutions powered by artificial
              intelligence. Transforming businesses through innovative software,
              cloud infrastructure, and strategic IT consulting.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-546">
              <Link to="/contact" className="group px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-black transition-all duration-300 flex items-center gap-2 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/50 ripple text-sm sm:text-base" data-id="element-547">
                Get a Quote
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-548" />
              </Link>
              <Link to="/services" className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white border-2 border-white/20 rounded-lg font-black transition-all duration-300 backdrop-blur-sm hover:scale-105 text-sm sm:text-base" data-id="element-549">
                Explore Services
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" data-id="element-550">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2" data-id="element-551">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse" data-id="element-552" />
          </div>
        </div>
      </section>

      {/* Stats Section with Animated Counters */}
      <section className="py-12 sm:py-20 bg-slate-50" data-id="element-553">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-554">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8" data-id="element-555">
            <StatCard icon={Award} value={12} suffix="+" label="Years of Excellence" delay={0} data-id="element-556" />
            <StatCard icon={TrendingUp} value={130} suffix="+" label="Successful Projects" delay={100} data-id="element-557" />
            <StatCard icon={Users} value={95} suffix="%" label="Client Satisfaction" delay={200} data-id="element-558" />
            <StatCard icon={Sparkles} value={30} suffix="+" label="Industry Experts" delay={300} data-id="element-559" />
          </div>
        </div>
      </section>

      {/* Interactive Services Preview */}
      <section className="py-16 sm:py-24 bg-white" data-id="element-560">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-561">
          <div className="text-center mb-12 sm:mb-16" data-id="element-562">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-4" data-id="element-563">
              Our Core Solutions
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-564">
              Comprehensive technology services designed to accelerate your
              digital transformation journey
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-id="element-565">
            <InteractiveServiceCard title="AI & Machine Learning" description="Predictive analytics, intelligent automation, and data-driven insights" icon={<Brain className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-567" />} image="https://images.unsplash.com/photo-1573164574511-73c773193279?q=80&w=2000&auto=format&fit=crop" link="/services#ai" data-id="element-566" />
            <InteractiveServiceCard title="Software Development" description="Custom web applications, mobile apps, and enterprise solutions" icon={<Zap className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-569" />} image="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2000&auto=format&fit=crop" link="/services#software" data-id="element-568" />
            <InteractiveServiceCard title="Cloud Solutions" description="Scalable infrastructure, migration, and cloud-native architecture" icon={<Shield className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-571" />} image="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=2000&auto=format&fit=crop" link="/services#cloud" data-id="element-570" />
            <InteractiveServiceCard title="Cybersecurity" description="Threat detection, data protection, and compliance management" icon={<Shield className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-573" />} image="https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=2000&auto=format&fit=crop" link="/services#security" data-id="element-572" />
            <InteractiveServiceCard title="Data Analytics" description="Business intelligence, dashboards, and actionable insights" icon={<TrendingUp className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-575" />} image="https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2000&auto=format&fit=crop" link="/services#analytics" data-id="element-574" />
            <InteractiveServiceCard title="IT Consulting" description="Strategic planning, digital transformation, and capacity building" icon={<Users className="w-10 h-10 sm:w-12 sm:h-12" data-id="element-577" />} image="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?q=80&w=2000&auto=format&fit=crop" link="/services#consulting" data-id="element-576" />
          </div>

          <div className="text-center mt-12" data-id="element-578">
            <Link to="/services" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-black text-base sm:text-lg group" data-id="element-579">
              View All Services
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-580" />
            </Link>
          </div>
        </div>
      </section>

      {/* Edo State Highlight with Parallax */}
      <ParallaxSection speed={0.3} className="py-16 sm:py-24 bg-gradient-to-br from-blue-900 to-slate-900 text-white" data-id="element-581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6" data-id="element-582">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center" data-id="element-583">
            <div data-id="element-584">
              <div className="inline-block px-4 py-2 bg-blue-500/20 border border-blue-400/30 rounded-full mb-6" data-id="element-585">
                <span className="text-sm font-black text-blue-300" data-id="element-586">
                  Featured Project
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-6" data-id="element-587">
                Edo State Digital Transformation
              </h2>
              <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-588">
                Leading the complete digitization of Edo State Government
                operations, delivering 78 MDA websites, 20 portals, and
                transforming citizen engagement through innovative technology
                solutions.
              </p>
              <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-8" data-id="element-589">
                <div data-id="element-590">
                  <div className="text-3xl sm:text-4xl font-black text-blue-400 mb-2" data-id="element-591">
                    <AnimatedCounter end={78} data-id="element-592" />
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400" data-id="element-593">
                    MDA Websites
                  </div>
                </div>
                <div data-id="element-594">
                  <div className="text-3xl sm:text-4xl font-black text-blue-400 mb-2" data-id="element-595">
                    <AnimatedCounter end={20} data-id="element-596" />
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400" data-id="element-597">
                    Portals Built
                  </div>
                </div>
                <div data-id="element-598">
                  <div className="text-3xl sm:text-4xl font-black text-blue-400 mb-2" data-id="element-599">
                    <AnimatedCounter end={100} suffix="%" data-id="element-600" />
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400" data-id="element-601">
                    Digital Coverage
                  </div>
                </div>
              </div>
              <Link to="/edo-case-study" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-black transition-all duration-300 hover:scale-105 ripple text-sm sm:text-base" data-id="element-602">
                View Case Study
                <ArrowRight className="w-5 h-5" data-id="element-603" />
              </Link>
            </div>
            <div className="relative" data-id="element-604">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl" data-id="element-605">
                <img src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?q=80&w=2000&auto=format&fit=crop" alt="Government digital transformation" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" data-id="element-606" />
              </div>
            </div>
          </div>
        </div>
      </ParallaxSection>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 bg-slate-50" data-id="element-607">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-608">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 mb-6" data-id="element-609">
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 mb-10" data-id="element-610">
            Let's discuss how our AI-powered solutions can drive your digital
            transformation
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-black text-base sm:text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/50 ripple" data-id="element-611">
            Start Your Journey
            <ArrowRight className="w-5 h-5" data-id="element-612" />
          </Link>
        </div>
      </section>
    </div>;
}
function StatCard({
  icon: Icon,
  value,
  suffix,
  label,
  delay
}: {
  icon: any;
  value: number;
  suffix: string;
  label: string;
  delay: number;
}) {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return <div className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} data-id="element-613">
      <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-2xl mb-4 group-hover:scale-110 transition-transform" data-id="element-614">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" data-id="element-615" />
      </div>
      <div className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" data-id="element-616">
        <AnimatedCounter end={value} suffix={suffix} data-id="element-617" />
      </div>
      <div className="text-xs sm:text-sm font-bold text-slate-600" data-id="element-618">{label}</div>
    </div>;
}
function InteractiveServiceCard({
  title,
  description,
  icon,
  image,
  link
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  image: string;
  link: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <Link to={link} className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-619">
      <div className="aspect-[4/3] relative overflow-hidden" data-id="element-620">
        <img src={image} alt={title} className={`w-full h-full object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`} data-id="element-621" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" data-id="element-622" />
      </div>
      <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end text-white" data-id="element-623">
        <div className={`mb-4 text-blue-400 transition-all duration-500 ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`} data-id="element-624">
          {icon}
        </div>
        <h3 className="text-xl sm:text-2xl font-black mb-3 group-hover:text-blue-400 transition-colors" data-id="element-625">
          {title}
        </h3>
        <p className={`text-sm sm:text-base text-slate-300 leading-relaxed transition-all duration-500 ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`} data-id="element-626">
          {description}
        </p>
      </div>
    </Link>;
}